/**
 * growth-api.ts — client for the S6 · Growth Validation routes (ADR-114).
 *
 *   GET  /admin/growth/cohorts
 *   POST /admin/growth/cohorts                            — Step 1, harvest
 *   GET  /admin/growth/cohorts/{cohortId}
 *   POST /admin/growth/cohorts/{cohortId}/approve         — the manual gate
 *   POST /admin/growth/cohorts/{cohortId}/score           — Step 2, ACT scoring
 *   GET  /admin/growth/cohorts/{cohortId}/cost-preview
 *   GET  /admin/growth/suspect-registry
 */
import { adminApi } from './api-client';

export type CohortStatus = 'harvested' | 'approved' | 'scoring' | 'scored';
export type Verdict = 'pass' | 'escalate' | 'reject' | 'fail_no_data' | 'unscored';
export type ContactPath = 'named_person' | 'role_account';
export type SectorKind = 'for_profit' | 'nfp_charity';

export interface Cohort {
  cohortId: string;
  label: string;
  sector: string;
  sectorKind: SectorKind;
  location: string;
  jurisdiction: string;
  requested: number;
  found: number;
  /** Pre-formatted "7 of 10" — see the note in `foundVsRequested` below. */
  foundVsRequested?: string;
  status: CohortStatus;
  createdAt: string;
  createdBy: string;
  serperCalls: number;
}

export interface Candidate {
  cohortId: string;
  candidateId: string;
  domain: string;
  businessName: string;
  verdict: Verdict;
  businessScore: number | null;
  emailScore: number | null;
  path: ContactPath | null;
  /**
   * ⭐ WHICH TIERS CONTRIBUTED. A score is not a comparable magnitude without this: a
   * record that escalated to T2/T4 buys more evidence AND a larger chain-of-evidence
   * bonus, so it can carry a HIGHER number than a stronger record that auto-passed early.
   * The table therefore always renders the tiers next to the score.
   */
  businessTiers: string[];
  emailTiers: string[];
  reason: string | null;
  /** T1's own tally. Always present once scored. */
  t1Score?: number | null;
  t1Green?: number;
  t1Red?: number;
  /** T2's own tally, ABSENT when T2 never ran — a deliberate skip, not a gap. */
  t2Score?: number | null;
  t2Green?: number;
  t2Red?: number;
  /** Group ids that returned no data — the operator's tooling-gap work list. */
  noDataGroups: string[];
  createdAt: string;
  /** Present only in the live scoring response; never persisted (the no-PII design). */
  notes?: Record<string, string>;
  /** Per-group points from the same live response; `null` where the group had no data. */
  groupScores?: Record<string, number | null>;

  // ── ADR-114 §7 advisory signals. None feed the ACT score. ──
  sector?: string;
  sizeBand?: 'micro' | 'small' | 'medium' | 'large' | 'unknown';
  headcount?: number | null;
  aggregatorSuspected?: boolean;
  aggregatorConfidence?: number;
  rejected?: boolean;
  /**
   * The BUSINESS track's own reason. `t1_auto_pass` is the pilot metric — see
   * `t1AutoPassRate`. Now PERSISTED, not just returned live, so the cohort rollup can be
   * computed from stored rows without re-scoring.
   */
  businessReason?: string;
  emailReason?: string;
  screenedOut?: boolean;
}

export interface HarvestRequest {
  sector: string;
  sectorKind: SectorKind;
  location: string;
  /**
   * The subdivision selected in the form (a US state, an AU state, an NZ region), sent
   * ALONGSIDE the composed `location` rather than parsed back out of it.
   *
   * ⭐⭐ THE REGISTER LOOKUP NEEDS A STATE AND `location` CANNOT SAFELY SUPPLY ONE once
   * county granularity is in play (D-009): county names collide with state names
   * constantly — Utah has a Washington county — so "Washington County, Utah, United States"
   * names two states to any text matcher and is correctly refused as ambiguous. `location`
   * is what the SEARCH is run on; `region` is what the REGISTER is asked about.
   *
   * Omitted for a free-typed location, which has no selected region.
   */
  region?: string;
  jurisdiction: string;
  targetCount: number;
  label?: string;
}

export interface HarvestResponse {
  cohort: Cohort;
  candidates: { domain: string; businessName: string; sourceUrl: string }[];
  shortfall: number;
  queriesRun: number;
}

/**
 * One scoring STEP — the endpoint scores a single candidate per call.
 *
 * ⭐ Measured: a candidate takes 20–25s against the real sources, and API Gateway's
 * integration timeout is a hard 30s. A whole-cohort call could never return to a browser.
 * Stepping also gives honest "3 of 10" progress and makes a run resumable.
 */
export interface ScoreStep {
  cohortId: string;
  /** Candidates completed SO FAR, including this one — render as `scored of total`. */
  scored: number;
  total: number;
  remaining: number;
  complete: boolean;
  serperCalls?: number;
  /** Null only when the cohort was already fully scored. */
  candidate: Candidate | null;
}

export interface CostPreview {
  cohortId: string;
  candidateCount: number;
  serperQueriesMin: number;
  serperQueriesMax: number;
  totalUsd: number;
  /** ⭐ Priced SEPARATELY and excluded from `totalUsd` — see the panel copy. */
  optIn: { asicExtractPerRecordUsd: number; asicExtractIfAllRecordsUsd: number };
}

export interface SuspectEntry {
  suspectKey: string;
  kind: 'domain' | 'template';
  pattern: string;
  reason: string;
  hitCount: number;
  firstSeenAt: string;
  lastSeenAt: string;
}

export const listCohorts = () =>
  adminApi<{ cohorts: Cohort[] }>('/admin/growth/cohorts');

export const getCohort = (cohortId: string) =>
  adminApi<{ cohort: Cohort; candidates: Candidate[] }>(
    `/admin/growth/cohorts/${encodeURIComponent(cohortId)}`);

export const harvestCohort = (body: HarvestRequest) =>
  adminApi<HarvestResponse>('/admin/growth/cohorts', {
    method: 'POST', body: JSON.stringify(body),
  });

export const approveCohort = (cohortId: string) =>
  adminApi<{ cohortId: string; status: CohortStatus }>(
    `/admin/growth/cohorts/${encodeURIComponent(cohortId)}/approve`, { method: 'POST' });

/** Score the NEXT unscored candidate. Call repeatedly until `complete`. */
export const scoreNextCandidate = (cohortId: string) =>
  adminApi<ScoreStep>(
    `/admin/growth/cohorts/${encodeURIComponent(cohortId)}/score`, { method: 'POST' });

/** Remove a cohort and its candidates. */
export const deleteCohort = (cohortId: string) =>
  adminApi<{ cohortId: string; deleted: number }>(
    `/admin/growth/cohorts/${encodeURIComponent(cohortId)}`, { method: 'DELETE' });

export const getCostPreview = (cohortId: string) =>
  adminApi<CostPreview>(`/admin/growth/cohorts/${encodeURIComponent(cohortId)}/cost-preview`);

export const listSuspects = () =>
  adminApi<{ suspects: SuspectEntry[] }>('/admin/growth/suspect-registry');

const candidatePath = (cohortId: string, candidateId: string) =>
  `/admin/growth/cohorts/${encodeURIComponent(cohortId)}`
  + `/candidates/${encodeURIComponent(candidateId)}`;

/** Reassign a candidate's sector — the correction mechanism for the aggregator flag (§7). */
export const reclassifyCandidate = (cohortId: string, candidateId: string, sector: string) =>
  adminApi<{ sector: string }>(candidatePath(cohortId, candidateId), {
    method: 'PATCH', body: JSON.stringify({ sector }),
  });

/**
 * Reject a business, or flag its email as suspect.
 *
 * ⚠ `target: 'email'` is STEP-2 ONLY — before a scoring pass there is no extracted address
 * to flag. The server enforces it (409); the UI hides it, which is a convenience, not the
 * guarantee.
 */
export const rejectCandidate = (
  cohortId: string, candidateId: string,
  target: 'business' | 'email' = 'business', note?: string,
) => adminApi<{ target: string }>(`${candidatePath(cohortId, candidateId)}/reject`, {
  method: 'POST', body: JSON.stringify({ target, note }),
});

/** Remove a candidate from the cohort, recording no judgement about it. */
export const removeCandidate = (cohortId: string, candidateId: string) =>
  adminApi<{ removed: boolean }>(candidatePath(cohortId, candidateId), { method: 'DELETE' });

/** Plain-English size band. `unknown` is honest, not a failure. */
export const sizeLabel = (band?: string): string => {
  switch (band) {
    case 'micro': return '< 10 staff';
    case 'small': return '10–49 staff';
    case 'medium': return '50–249 staff';
    case 'large': return '250+ staff';
    default: return 'Unknown';
  }
};

/** Sort order for the size column — `unknown` sorts last, not as zero. */
export const sizeRank = (band?: string): number =>
  ({ micro: 0, small: 1, medium: 2, large: 3 } as Record<string, number>)[band ?? ''] ?? 99;

// =============================================================================
// Plain-English labels
// =============================================================================
//
// Machine tokens (`fail_no_data`, `role_account`, `register_lookup`) live in the type
// system and the API — never in the DOM (admin-portal-ui-standards §plain-English).

export function verdictLabel(v: Verdict): string {
  switch (v) {
    case 'pass': return 'Pass';
    case 'escalate': return 'Needs review';
    case 'reject': return 'Reject';
    // ⭐ NOT "Fail". The distinction is the operator's whole action: a rejected record is a
    // bad target to drop; an unscoreable one is a TOOLING GAP to fix, and the same record
    // may well pass once the missing source is provisioned.
    case 'fail_no_data': return 'Not enough data';
    case 'unscored': return 'Not yet scored';
  }
}

export function verdictTone(v: Verdict): 'success' | 'warning' | 'danger' | 'neutral' {
  switch (v) {
    case 'pass': return 'success';
    case 'escalate': return 'warning';
    case 'reject': return 'danger';
    case 'fail_no_data': return 'warning';
    case 'unscored': return 'neutral';
  }
}

export function pathLabel(p: ContactPath | null): string {
  if (p === 'named_person') return 'Named person';
  if (p === 'role_account') return 'Role account';
  return '—';
}

/** Group id → the plain-English name used in the rubric. */
export const GROUP_LABELS: Record<string, string> = {
  register_lookup: 'Business register',
  wayback_history: 'Site history',
  domain_certificate: 'Domain and certificate',
  site_content_structure: 'Site content and structure',
  citation_consistency: 'Listing consistency',
  directory_presence: 'Directory membership',
  dns_confidence: 'Mail and DNS setup',
  address_plausibility: 'Address plausibility',
  indexed_presence: 'Search indexing',
  verified_entity_signals: 'Verified business profile',
  reputation_reviews: 'Reviews and reputation',
  testimonial_validation: 'Testimonial validation',
  seo_backlinks: 'Backlink profile',
  social_footprint: 'Social footprint',
  successor_role_currency: 'Contact still in role',
  role_prefix_classification: 'Recognised role prefix',
  email_structure: 'Address structure',
  catch_all_domain: 'Individually provisioned mailbox',
  mail_provider_inference: 'Mail provider',
  osint_footprint: 'Online presence',
  blocklist_screen: 'Suspected-registry screen',
  domain_match: 'Domain match',
  visible_extraction: 'Address visibility',
};

export const groupLabel = (id: string): string => GROUP_LABELS[id] ?? id;

/**
 * Which tier each group belongs to.
 *
 * ⭐⭐ THE TIER IS NOT DECORATION — IT IS WHICH SCORE THE GAP AFFECTS. Business ACT pools
 * T1+T2 and Email ACT pools T3+T4, and the no-score fail rule counts ">2 no-data groups"
 * SEPARATELY within each tier-set. Without the tier on screen, an operator cannot tell
 * whether three gaps are three-in-one-set (fatal) or spread across both (harmless) — and
 * during this UAT a T4 group was reported as a T1 gap for exactly that reason.
 */
export const GROUP_TIERS: Record<string, 'T1' | 'T2' | 'T3' | 'T4'> = {
  register_lookup: 'T1', wayback_history: 'T1', domain_certificate: 'T1',
  site_content_structure: 'T1', citation_consistency: 'T1', directory_presence: 'T1',
  dns_confidence: 'T1', address_plausibility: 'T1', indexed_presence: 'T1',
  blocklist_screen: 'T1',
  verified_entity_signals: 'T2', reputation_reviews: 'T2', testimonial_validation: 'T2',
  seo_backlinks: 'T2', social_footprint: 'T2',
  successor_role_currency: 'T3', role_prefix_classification: 'T3', email_structure: 'T3',
  catch_all_domain: 'T3', mail_provider_inference: 'T3', domain_match: 'T3',
  visible_extraction: 'T3',
  osint_footprint: 'T4',
};

export const groupTier = (id: string): string => GROUP_TIERS[id] ?? '—';

/** Which score a tier feeds — Business ACT is T1+T2, Email ACT is T3+T4. */
export const tierTrack = (id: string): 'Business' | 'Email' | null => {
  const t = GROUP_TIERS[id];
  if (t === 'T1' || t === 'T2') return 'Business';
  if (t === 'T3' || t === 'T4') return 'Email';
  return null;
};

/**
 * Each group's maximum, so a score can be shown as "16/20" rather than a bare number.
 *
 * ⚠ THIS IS A HAND-KEPT MIRROR OF `lambdas/growth/src/act/rubric.ts`, and it is the THIRD
 * place the group maxima were written down. The other two diverged: `wayback.ts` hardcoded
 * 30, the framework demoted the group to 12 (§4.3, 31 July 2026), and production awarded
 * 30 points for a 12-point group. This file held the same stale 30, so the operator would
 * have read "10 / 30" for a group worth 12 — the display agreeing with the bug rather than
 * catching it.
 *
 * The Lambda side now derives every max from the rubric. The portal cannot import across
 * that boundary, so this copy is guarded by a test that asserts it matches the rubric
 * exactly — the mirror is allowed to exist, but not to drift.
 */
export const GROUP_MAX: Record<string, number> = {
  register_lookup: 50, wayback_history: 12, domain_certificate: 20,
  site_content_structure: 20, citation_consistency: 15, directory_presence: 15,
  dns_confidence: 10, address_plausibility: 10, indexed_presence: 8,
  verified_entity_signals: 30, reputation_reviews: 25, testimonial_validation: 25,
  seo_backlinks: 20, social_footprint: 12,
  successor_role_currency: 25, role_prefix_classification: 35, email_structure: 22,
  catch_all_domain: 15, mail_provider_inference: 12, osint_footprint: 35,
};

/** The universal pass mark for BOTH scores (framework §8, §11). */
export const PASS_MARK = 100;

/**
 * ⭐⭐ THE PILOT METRIC — share of candidates that cleared 100 on T1 ALONE.
 *
 * T1 is the free tier; T2 is the paid one. This percentage is what drives T2 volume and
 * cost planning, so it must be readable on the cohort without opening a single row.
 *
 * ⚠ Denominator is SCORED candidates, not all candidates. Including unscored rows would
 * make the rate drift downward purely because scoring is mid-run, which is exactly when
 * someone would be watching it.
 */
export function t1AutoPassRate(candidates: Candidate[]): {
  passed: number; scored: number; pct: number | null;
} {
  const scored = candidates.filter((c) => c.verdict !== 'unscored');
  const passed = scored.filter((c) => c.businessReason === 't1_auto_pass').length;
  return {
    passed,
    scored: scored.length,
    // null, not 0 — "no data yet" and "0% passed" are different facts.
    pct: scored.length === 0 ? null : Math.round((passed / scored.length) * 100),
  };
}

export function suspectReasonLabel(reason: string): string {
  const base = reason.split(' ')[0];
  switch (base) {
    case 'trap_farm_template': return 'Reused trap-farm template';
    case 'hidden_extraction': return 'Address hidden from human readers';
    case 'improbable_localpart': return 'Algorithmic address shape';
    case 'blocklist_screen': return 'Blocklist hit';
    default: return reason;
  }
}

/**
 * "7 of 10".
 *
 * ⭐ ALWAYS RENDERED AS A PAIR. ADR-114 §5 requires found-vs-requested rather than a
 * silently under-filled list: padding to hit the target would destroy the operator's
 * ability to judge whether the search criteria were any good, which is the entire purpose
 * of the manual approval gate that follows Step 1.
 */
export const foundVsRequested = (c: Cohort): string =>
  c.foundVsRequested ?? `${c.found} of ${c.requested}`;

// =============================================================================
// Connections — the four data connectors' API keys (D-009, Track 0)
// =============================================================================

export type ConnectorId = 'abr' | 'nzbn' | 'opensosdata' | 'census';

export interface ConnectorStatus {
  id: ConnectorId;
  label: string;
  /** What the connector is FOR, in the operator's language. */
  purpose: string;
  signupUrl: string;
  /** Honest cost line — free is stated as plainly as paid. */
  cost: string;
  /**
   * Whether a usable key is stored.
   *
   * ⛔ THIS IS THE ONLY THING THE SERVER SAYS ABOUT THE VALUE. There is no key, no masked
   * prefix and no length in this type, and adding one would be a real regression rather
   * than a nicety: these are bearer tokens for third-party APIs, and the operator who
   * pasted one does not need it read back. See `lambdas/growth/src/connections.ts`.
   */
  configured: boolean;
  lastUpdated: string | null;
  /** Set only when the WIRING is wrong (a missing env var), never when a key is absent. */
  problem?: string;
}

export const listConnections = () =>
  adminApi<{ connectors: ConnectorStatus[] }>('/admin/growth/connections');

/**
 * Store a pasted key.
 *
 * ⚠ The connector is identified by `id`, never by secret name — the server resolves the
 * name through its own allowlist. A client that could name the secret would be a client
 * that could overwrite any secret in the account.
 */
export const setConnectionKey = (id: ConnectorId, apiKey: string) =>
  adminApi<{ id: ConnectorId; configured: boolean }>(
    `/admin/growth/connections/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify({ apiKey }) },
  );

// =============================================================================
// G0 · Business discovery (ADR-121)
// =============================================================================

export type WorkItemKind = 'brief' | 'directive';
export type BriefStatus = 'queued' | 'in_progress' | 'fulfilled' | 'cancelled';

export interface DiscoveryWorkItem {
  itemId: string;
  kind: WorkItemKind;
  status: BriefStatus;
  countryCode: string;
  regionCode: string;
  countyFips?: string;
  locality?: string;
  sector: string;
  targetCount: number;
  foundCount: number;
  priority: number;
  /** Directives only — a paused directive keeps its history and its counters. */
  enabled?: boolean;
  requestedBy: string;
  createdAt: string;
  updatedAt: string;
  lastRunAt?: string;
  /**
   * ⭐ The addressable market for this county + industry, from Census County Business
   * Patterns — a real government aggregate, NEVER a count of what G0 has found. "How many
   * have we found" is exactly the number that cannot tell you whether the search was worth
   * running.
   */
  tamEstimate?: number;
}

export interface DiscoveryStats {
  businessesIndexed: number;
  geoResolved: number;
  competitorsFlagged: number;
  alreadyKnown: number;
  runs: number;
  lastRunAt?: string;
  /**
   * ⭐⭐ THE PRIMARY GEO TIER'S STATE, AND THE REASON THIS FIELD EXISTS AT ALL.
   * `not_enabled` means the Google Cloud project has no Maps API switched on — an operator
   * action in Google's console, not a missing key and not a business without a profile.
   * Collapsed into "no match", a whole country's discoveries would fall through to the
   * site fallback and the pipeline would look entirely healthy.
   */
  lastPlacesStatus?: string;
}

export interface CompetitorRegistryEntry {
  domain: string;
  classification: 'competitor' | 'market_intelligence' | 'data_broker';
  reason: string;
  confidence: 'low' | 'medium' | 'high';
  classifiedAt: string;
  source: string;
}

export const getDiscoveryStatus = () =>
  adminApi<{ stats: DiscoveryStats; briefs: DiscoveryWorkItem[]; directives: DiscoveryWorkItem[] }>(
    '/admin/growth/discovery',
  );

export interface NewWorkItemRequest {
  kind: WorkItemKind;
  countryCode: 'US' | 'AU' | 'NZ';
  regionCode: string;
  countyFips?: string;
  locality?: string;
  sector: string;
  targetCount: number;
  priority?: number;
}

export const createWorkItem = (req: NewWorkItemRequest) =>
  adminApi<{ item: DiscoveryWorkItem }>('/admin/growth/discovery/queue', {
    method: 'POST', body: JSON.stringify(req),
  });

/** Pause/resume a standing directive, or cancel a queued brief. */
export const updateWorkItem = (
  itemId: string, patch: { enabled?: boolean; status?: 'cancelled' },
) =>
  adminApi<{ item: DiscoveryWorkItem }>(
    `/admin/growth/discovery/queue/${encodeURIComponent(itemId)}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
  );

export const listCompetitorRegistry = (
  classification: CompetitorRegistryEntry['classification'],
) =>
  adminApi<{ entries: CompetitorRegistryEntry[] }>(
    `/admin/growth/discovery/competitors?classification=${encodeURIComponent(classification)}`,
  );
